import { calculateReservationNights } from 'Utils/dateTimeHelpers';
import moment from 'moment';
import PRODUCT_REF_TYPE from 'Constants/productRefType';

export function doesNOTAffectCharge(item, initialItem, duration) {
  if (item.discount > 0 && item.maxQuantityForDiscount && item.maxQuantityForDiscount > 0) {
    return daysWithoutDiscountByMaxLimit(item, duration) <= 0;
  } else {
    return daysWithoutDiscount(item, initialItem.startDate, initialItem.endDate, duration) <= 0;
  }
}

export function calculateTotalForItem(item, initialItem, duration) {
  if (item.discount > 0 && item.maxQuantityForDiscount && item.maxQuantityForDiscount > 0) {
    const days = initialItem.nightly ? Math.abs(daysWithoutDiscountByMaxLimit(item, duration)) : 1;
    return initialItem.price * days;
  } else {
    const price = item.quantity * initialItem.price - initialItem.quantity * initialItem.price;
    const days = initialItem.nightly ? Math.abs(daysWithoutDiscount(item, initialItem.startDate, initialItem.endDate, duration)) : 1;
    return price * days;
  }
}

export function daysWithoutDiscount(item, initialStartDate, initialEndDate, initialDuration) {
  if ((!item.discountStartDate || !item.discountEndDate) && initialDuration) return initialDuration;

  const selectedDatesRange = moment.range(moment(initialStartDate), moment(initialEndDate));
  const discountRange = moment.range(moment(item.discountStartDate), moment(item.discountEndDate));

  const selectedDatesArray = Array.from(selectedDatesRange.by('days'));

  const daysWithoutDiscount = selectedDatesArray.filter(selectedDate => !discountRange.contains(moment(selectedDate)));

  return daysWithoutDiscount.length;
}

export function daysWithoutDiscountByMaxLimit(item, initialDuration) {
  if ((!item.discountStartDate || !item.discountEndDate) && initialDuration) return initialDuration;

  const selectedDatesRange = moment.range(moment(item.startDate), moment(item.endDate).subtract(1, 'day'));
  const discountRange = moment.range(moment(item.discountStartDate), moment(item.discountEndDate));

  const selectedDatesArray = Array.from(selectedDatesRange.by('days'));

  const discountDays = selectedDatesArray.filter(selectedDate => discountRange.contains(moment(selectedDate)));

  const totalItemsWithDiscount = Math.min(item.maxQuantityForDiscount, item.quantity);
  const totalDaysWithDiscount = discountDays.length * totalItemsWithDiscount;

  if (totalDaysWithDiscount >= selectedDatesArray.length * item.quantity) {
    return 0;
  }

  const remainingItems = item.quantity - totalItemsWithDiscount;
  return selectedDatesArray.length * remainingItems;
}

export function datesDifferenceCount(item, initialStartDate, initialEndDate) {
  const selectedDatesRange = moment.range(moment(item.startDate), moment(item.endDate));
  const oldDatesRange = moment.range(moment(initialStartDate), moment(initialEndDate));

  const discountRange = item.discountStartDate && item.discountEndDate ? moment.range(moment(item.discountStartDate), moment(item.discountEndDate)) : null;

  const newDatesArray = Array.from(selectedDatesRange.by('days'));
  const oldDatesArray = Array.from(oldDatesRange.by('days'));

  const newDaysWithoutDiscount = discountRange ? newDatesArray.filter(date => !discountRange.contains(date)) : newDatesArray;

  const oldDaysWithoutDiscount = discountRange ? oldDatesArray.filter(date => !discountRange.contains(date)) : oldDatesArray;

  return newDaysWithoutDiscount.length - oldDaysWithoutDiscount.length;
}

export function parseDifferencesByItemWithDiscounts(priceAdjustments, initialOrderCosts, orderCosts) {
  if (!priceAdjustments || !priceAdjustments.length) return [];

  return priceAdjustments.map(priceAdjustment => {
    if (!initialOrderCosts || !orderCosts) return priceAdjustment;

    const initialProduct = initialOrderCosts.orderItemsCostsWithDetails.find(item => item.xProductId === priceAdjustment[3]);
    const currentProduct = orderCosts.orderItemsCostsWithDetails.find(item => item.xProductId === priceAdjustment[3]);

    if (initialProduct && !currentProduct && initialProduct.discount && initialProduct.discount > 0) {
      let newPriceAdjustment = [...priceAdjustment];
      newPriceAdjustment[1] = newPriceAdjustment[1] + initialProduct.discount;
      return newPriceAdjustment;
    } else {
      return priceAdjustment;
    }
  });
}

export const calculateDifferencesByItem = (values, orderCosts) => {
  if (!orderCosts || !orderCosts.orderCosts) return [];
  const productChanges = [];
  const initialItemsHash = {};
  const initialOrderItems = values.initialOrder.orderItems;
  const currentOrderItems = orderCosts.orderCosts?.orderItemsCostsWithDetails;
  const orderItemsHash = {};

  const pluralizeName = (diff, quantity, name) => {
    if (diff > 1 && quantity > 1) {
      return `${name}`;
    } else if (quantity > 1) {
      return `${name}s`;
    } else if (quantity === 1) {
      return name;
    }
    return name.slice(0, -1);
  };

  // build hash of initial items
  initialOrderItems.forEach(item => {
    const { quantity } = item;
    if (item.addOnProduct && quantity > 0) {
      const { id, price } = item.addOnProduct;
      initialItemsHash[id] = { quantity, cost: quantity * price, xRefTypeId: '2' };
    } else if (item.reservation && quantity > 0) {
      const product = item.reservation.rvProduct ? 'rvProduct' : 'stallProduct';
      const { id, price, nightly } = item.reservation[product];
      const { startDate, endDate } = item.reservation;
      const reservationNights = calculateReservationNights(item.reservation);
      const cost = nightly ? quantity * price * reservationNights : quantity * price;
      initialItemsHash[id] = { quantity, price, cost, startDate, endDate, nightly, product, xRefTypeId: product === 'rvProduct' ? '3' : '1' };
    }
  });

  // check for differences with current items
  currentOrderItems?.forEach(item => {
    const id = item.xProductId;
    const xRefTypeId = item.xRefTypeId;
    const typeName = item.xRefTypeId == PRODUCT_REF_TYPE.RV_PRODUCT ? 'spot' : 'stall';
    orderItemsHash[id] = true;
    let name, nights, initialDuration;

    // StallProduct and RVProduct can have the same ID!
    const parsedInitialItemsHash = Object.fromEntries(Object.entries(initialItemsHash).filter(([, values]) => values.xRefTypeId === xRefTypeId));

    if (!item.startDate) {
      const matchingAddOn = values.event.addOnProducts.find(addOn => addOn.id === item.xProductId);
      name = matchingAddOn?.addOn.unitName;
    } else if (parsedInitialItemsHash[id]) {
      name = parsedInitialItemsHash[id]?.product === 'rvProduct' ? 'spot' : 'stall';
      nights = moment(item.endDate).diff(moment(item.startDate), 'days');
      initialDuration = moment(parsedInitialItemsHash[id].endDate).diff(moment(parsedInitialItemsHash[id].startDate), 'days');
    } else if (!parsedInitialItemsHash[id]) {
      name = typeName;
      nights = moment(item.endDate).diff(moment(item.startDate), 'days');
    }

    name += parsedInitialItemsHash[id]?.quantity - item.quantity > 1 ? 's' : '';

    const dateWasChanged =
      item.startDate &&
      parsedInitialItemsHash[id] &&
      (item.startDate !== parsedInitialItemsHash[id].startDate || item.endDate !== parsedInitialItemsHash[id].endDate);

    if (!parsedInitialItemsHash[id]) {
      productChanges.push([
        `Added ${item.quantity} ${name}${nights ? ` x ${nights} nights` : ''}`,
        item.orderItemCost,
        !!item.discount && item.orderItemCost <= item.discount && item.discount > 0,
        id
      ]);
    } else if (item.quantity == 0) {
      productChanges.push([
        `Removed ${parsedInitialItemsHash[id].quantity} ${name}${nights ? ` x ${nights} nights` : ''}`,
        item.orderItemCost,
        !!item.discount && item.orderItemCost <= item.discount && item.discount > 0,
        id
      ]);
    } else if (item.quantity !== parsedInitialItemsHash[id].quantity) {
      if (dateWasChanged) {
        // qty and dates changed at the same time
        const nightsNumber = item.quantity < parsedInitialItemsHash[id].quantity ? initialDuration : nights;
        productChanges.push([
          `${item.quantity > parsedInitialItemsHash[id].quantity ? 'Added' : 'Removed'} ${Math.abs(
            item.quantity - parsedInitialItemsHash[id].quantity
          )} ${name}${nightsNumber ? ` x ${nightsNumber} ${nightsNumber > 1 ? 'nights' : 'night'}` : ''}`,
          !item.startDate ? item.orderItemCost - parsedInitialItemsHash[id].cost : calculateTotalForItem(item, parsedInitialItemsHash[id], nightsNumber),
          doesNOTAffectCharge(item, parsedInitialItemsHash[id], nightsNumber),
          id
        ]);
      } else {
        productChanges.push([
          `${item.quantity > parsedInitialItemsHash[id].quantity ? 'Added' : 'Removed'} ${Math.abs(
            item.quantity - parsedInitialItemsHash[id].quantity
          )} ${name}${initialDuration ? ` x ${initialDuration} ${initialDuration > 1 ? 'nights' : 'night'}` : ''}`,
          !item.startDate ? item.orderItemCost - parsedInitialItemsHash[id].cost : calculateTotalForItem(item, parsedInitialItemsHash[id], initialDuration),
          doesNOTAffectCharge(item, parsedInitialItemsHash[id], initialDuration),
          id
        ]);
      }
    }

    if (dateWasChanged) {
      const updatedDateSameNights = nights === initialDuration;
      const isDateReduction = nights < initialDuration;
      const isDateExtension = nights > initialDuration;
      const qtyDiff = Math.abs(item.quantity - parsedInitialItemsHash[id].quantity);
      const nightsDiff = Math.abs(nights - initialDuration);
      const pluralizedName = pluralizeName(qtyDiff, item.quantity, name);
      const pluralizedNight = nightsDiff > 1 ? 'nights' : 'night';
      const isQtyIncreased = item.quantity > parsedInitialItemsHash[id].quantity;
      let updatedItemsDescription = '';
      let itemQuantity = isQtyIncreased ? parsedInitialItemsHash[id].quantity : item.quantity;

      if (updatedDateSameNights) {
        updatedItemsDescription = 'Updated date - Same number of nights';
      }
      if (isDateReduction) {
        updatedItemsDescription = `Reduced ${item.quantity} ${pluralizedName} x ${nightsDiff} ${pluralizedNight}`;
      }
      if (isDateReduction && isQtyIncreased) {
        updatedItemsDescription = `Reduced ${parsedInitialItemsHash[id].quantity} ${pluralizedName} x ${nightsDiff} ${pluralizedNight}`;
      }
      if (isDateExtension) {
        updatedItemsDescription = `Extended ${item.quantity} ${pluralizedName} x ${nightsDiff} ${pluralizedNight}`;
      }
      if (isDateExtension && isQtyIncreased) {
        updatedItemsDescription = `Extended ${parsedInitialItemsHash[id].quantity} ${pluralizedName} x ${nightsDiff} ${pluralizedNight}`;
      }

      productChanges.push([
        updatedItemsDescription,
        parsedInitialItemsHash[id].nightly === false
          ? 0
          : itemQuantity *
            parsedInitialItemsHash[id].price *
            datesDifferenceCount(item, parsedInitialItemsHash[id].startDate, parsedInitialItemsHash[id].endDate),
        datesDifferenceCount(item, parsedInitialItemsHash[id].startDate, parsedInitialItemsHash[id].endDate) === 0,
        id
      ]);
    }

    if (initialItemsHash[id] && item.xRefTypeId === initialItemsHash[id].xRefTypeId) {
      delete initialItemsHash[id];
    }
  });
  // End of currentOrderItems loop

  // check original items to see if anything was deleteed
  Object.keys(initialItemsHash).forEach(item => {
    const currentItem = currentOrderItems?.find(orderItem => {
      return item === orderItem.xProductId;
    });

    if (!currentItem) {
      let name, nights;
      if (!initialItemsHash[item].startDate) {
        const matchingAddOn = values.event.addOnProducts.find(addOn => addOn.id === item);
        name = matchingAddOn?.addOn.unitName;
      } else {
        name = initialItemsHash[item].product === 'rvProduct' ? 'spot' : 'stall';
        nights = moment(initialItemsHash[item].endDate).diff(moment(initialItemsHash[item].startDate), 'days');
      }
      name += initialItemsHash[item].quantity > 1 ? 's' : '';
      productChanges.push([
        `Removed ${initialItemsHash[item].quantity} ${name}${nights ? ` x ${nights} nights` : ''}`,
        initialItemsHash[item].cost * -1,
        false,
        item
      ]);
    }
  });

  return productChanges;
};
